import { gql } from '@apollo/client'
import { Pagination } from 'antd'
import Image from 'next/image'
import Link from 'next/link'
import { useRouter } from 'next/router'
import { useCallback, useEffect, useState } from 'react'
import { useTranslation } from 'src/utils/i18n'
import { logQuery } from 'src/utils/apollo'
import Bottom from 'src/components/guide/Bottom'
import Header from 'src/components/guide/Header'
import styles from './index.module.scss'

const Guide = () => {
  const { t, locale } = useTranslation('common')
  const [searchText, setSearchText] = useState('')
  const [currentAndroid, setCurrentAndroid] = useState(1)
  const [currentIos, setCurrentIos] = useState(1)
  const [dataAndroid, setDataAndroid] = useState([])
  const [totalAndroid, setTotalAndroid] = useState(0)
  const [dataIos, setDataIos] = useState([])
  const [totalIos, setTotalIos] = useState(0)
  const router = useRouter()

  const getAndroidData = useCallback(
    page => {
      // 历史「找 Google 账号」深链文章，不在首页展示
      const _arr = [
        'get-someones-google-account-and-password',
        'get-someones-google-account-and-password-jp',
        'google-konto-und-passwort-von-jemandem-erhalten',
        'obtenir-le-compte-et-le-mot-de-passe-google-de-quelquun',
        'obtener-la-cuenta-de-google-y-la-contrasena-de-alguien',
        'ottenere-qualcuno-account-e-password-google',
        'obter-a-conta-e-a-palavra-passe-de-alguem-no-google',
        'get-someones-google-account-and-password-kr',
        'referral-program-terms-and-conditions'
      ]
      let category = 'android-guide'
      let local = 'en'
      if (locale && locale != 'en') {
        if (locale == 'it-ch') {
          local = 'it'
          category = category + '-it'
        } else if (locale == 'jp') {
          local = 'ja-JP'
          category = category + '-jp'
        } else if (locale == 'kr') {
          local = 'ko-KR'
          category = category + '-kr'
        } else {
          local = locale
          category = category + '-' + locale
        }
      }

      // android-guide 已空；实际指南在 dashboards（user-guide-android / back-up-android）
      logQuery({
        query: gql`
        query {
            tsafelyArticles(
              filters: { tsafely_category: { name: { in: ["${category}", "dashboards"] } } }
              pagination: { page: ${page}, pageSize: 20 }
              sort: "updatedAt:desc"
              locale: "${local}"
            ) {
              data {
                id
                attributes {
                  title
                  slug
                }
              }
              meta {
                pagination {
                  page
                  pageSize
                  pageCount
                  total
                }
              }
            }
          }
        `
      }).then(res => {
        const list = res.data.tsafelyArticles.data.filter(item => {
          const slug = item.attributes.slug || ''
          if (_arr.includes(slug)) return false

          return slug.includes('android')
        })
        setDataAndroid(list.slice(0, 6))
        setTotalAndroid(list.length)
      })
    }, [locale]
  )

  const getIosData = useCallback(
    page => {
      const _arr = [
        'get-someones-icloud-account-and-password',
        'get-someones-icloud-account-and-password-jp',
        'icloud-konto-und-passwort-von-jemandem-erhalten',
        'obtenir-le-compte-et-le-mot-de-passe-de-quelquun-sur-icloud',
        'conseguir-la-cuenta-y-contrasena-de-alguien-en-icloud',
        'ottenere-qualcuno-account-e-password-icloud',
        'obter-conta-e-senha-de-alguem-da-icloud',
        'get-someones-icloud-account-and-password-kr',
        'referral-program-terms-and-conditions'
      ]
      let category = 'ios-guide'
      let local = 'en'
      if (locale && locale != 'en') {
        if (locale == 'it-ch') {
          local = 'it'
          category = category + '-it'
        } else if (locale == 'jp') {
          local = 'ja-JP'
          category = category + '-jp'
        } else if (locale == 'kr') {
          local = 'ko-KR'
          category = category + '-kr'
        } else {
          local = locale
          category = category + '-' + locale
        }
      }

      logQuery({
        query: gql`
          query {
            tsafelyArticles(
              filters: { tsafely_category: { name: { in: ["${category}", "dashboards"] } } }
              pagination: { page: ${page}, pageSize: 20 }
              sort: "updatedAt:desc"
              locale: "${local}"
            ) {
              data {
                id
                attributes {
                  title
                  slug
                }
              }
              meta {
                pagination {
                  page
                  pageSize
                  pageCount
                  total
                }
              }
            }
          }
        `
      }).then(res => {
        const list = res.data.tsafelyArticles.data.filter(item => {
          const slug = item.attributes.slug || ''
          if (_arr.includes(slug)) return false

          return slug.includes('ios') || slug.includes('iphone') || slug.includes('icloud')
        })
        setDataIos(list.slice(0, 6))
        setTotalIos(list.length)
      })
    }, [locale]
  )

  useEffect(() => {
    getAndroidData(1)
    getIosData(1)
  }, [getAndroidData, getIosData])

  const onChangeAndorid = page => {
    setCurrentAndroid(page)
    getAndroidData(page)
  }

  const onChangeIos = page => {
    setCurrentIos(page)
    getIosData(page)
  }

  const onEnter = event => {
    if (event.key === 'Enter') {
      router.push('/guide/search?query=' + searchText)
    }
  }

  return (
    <>
      <Header />
      <div className='w-full h-[240px] Bxl:px-[16px] bg-[#1c1e53] border-t border-[rgb(118,118,120)] flex flex-col justify-center items-center'>
        <h1 className='text-[26px] Bld:text-[22px] text-[#FFF] text-center font-[600]'>
          {t('Welcome to Msafely Help Center')}!
        </h1>
        <div className='w-[607px] Bld:w-full h-[44px] Bld:h-[40px] bg-[#FFF] border border-[rbg(121,121,121)] rounded-[41px] outline-none px-[24px] mt-[30px] flex items-center'>
          <Image src='/images/contacts/search.svg' width={20} height={0} alt='search' />
          <input
            className='ml-[10px] flex-1 outline-none text-[16px] placeholder:text-[rgb(129,129,135)]'
            placeholder={t('Search')}
            value={searchText}
            onChange={e => setSearchText(e.target.value)}
            onKeyPress={onEnter}
          />
        </div>
      </div>
      <main className='w-full min-h-[calc(var(--vh)*100-620px)] Bld:min-h-[calc(var(--vh)*100-600px)] Bxl:px-[16px]'>
        <div className='max-w-[1200px] mx-auto py-[40px] flex Bld:flex-col'>
          <div className='flex-1 flex flex-col justify-between'>
            <div>
              <h2 className='text-[20px] Bld:text-[18px] font-[600]'>{t('User guide for Android')}</h2>
              <div className='mt-[10px] flex flex-col w-[85%] Blds:Bxl:w-[90%] Bld:w-full'>
                {dataAndroid.map((item, i) => (
                  <div key={i} className='py-[20px] border-b'>
                    <Link
                      href={'/guide/' + item.attributes.slug}
                      className='text-[16px] Bld:text-[15px] text-[#222] hover:text-[#2a99ff] cursor-pointer'
                    >
                      {item.attributes.title}
                    </Link>
                  </div>
                ))}
              </div>
            </div>
            <Pagination
              className={`${styles.pagination} mt-[30px]`}
              current={currentAndroid}
              pageSize={6}
              total={totalAndroid}
              onChange={onChangeAndorid}
            />
          </div>
          <div className='flex-1 Bld:mt-[50px] flex flex-col justify-between'>
            <div>
              <h2 className='text-[20px] Bld:text-[18px] font-[600]'>{t('User guide for iPhoneiPad')}</h2>
              <div className='mt-[10px] flex flex-col w-[85%] Blds:Bxl:w-[90%] Bld:w-full'>
                {dataIos.map((item, i) => (
                  <div key={i} className='py-[20px] border-b'>
                    <Link
                      href={'/guide/' + item.attributes.slug}
                      className='text-[16px] Bld:text-[15px] text-[#222] hover:text-[#2a99ff] cursor-pointer'
                    >
                      {item.attributes.title}
                    </Link>
                  </div>
                ))}
              </div>
            </div>
            <Pagination
              className={`${styles.pagination} mt-[30px]`}
              current={currentIos}
              pageSize={6}
              total={totalIos}
              onChange={onChangeIos}
            />
          </div>
        </div>
      </main>
      <Bottom />
    </>
  )
}

export default Guide
